import type { PaymentMethodSetupApi, PaymentMethodSetupKind, SavedPaymentMethod } from '../types';
import { isSavedPaymentMethodComplete } from './saved-payment-method';

const PAYMENT_METHOD_POLL_DELAYS = [1000, 2000, 3000, 4000];

const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

export interface PollPaymentMethodArgs {
  getPaymentMethod: PaymentMethodSetupApi['getPaymentMethod'];
  paymentMethodId: string;
  kind: PaymentMethodSetupKind;
  delays?: number[];
  wait?: (ms: number) => Promise<void>;
  onRetry?: (attempt: number) => void;
}

/** Polls until the backend has synced the new method from the processor. */
export const pollPaymentMethod = async ({
  getPaymentMethod,
  paymentMethodId,
  kind,
  delays = PAYMENT_METHOD_POLL_DELAYS,
  wait = sleep,
  onRetry,
}: PollPaymentMethodArgs): Promise<SavedPaymentMethod | undefined> => {
  for (let attempt = 0; attempt < delays.length; attempt++) {
    await wait(delays[attempt]);
    if (attempt > 0) onRetry?.(attempt);
    try {
      const method = await getPaymentMethod(paymentMethodId, kind);
      if (method) return method;
    } catch {
      // Not synced yet; retry.
    }
  }
  return undefined;
};

/** Polls for the new method, then checks it is the expected kind with every masked detail. */
export const fetchSavedPaymentMethod = async (
  args: PollPaymentMethodArgs
): Promise<{ method: SavedPaymentMethod; error?: undefined } | { method?: undefined; error: SavedMethodError }> => {
  const method = await pollPaymentMethod(args);
  if (!method) return { error: 'details_not_found' };
  if (method.kind !== args.kind || !isSavedPaymentMethodComplete(method)) return { error: 'details_incomplete' };
  return { method };
};

type SavedMethodError = 'details_not_found' | 'details_incomplete';

/** The id from a confirmed SetupIntent's `payment_method`, which may be expanded. */
export const getPaymentMethodId = (paymentMethod: string | { id: string } | null | undefined) =>
  (typeof paymentMethod === 'string' ? paymentMethod : paymentMethod?.id) || undefined;
